import { createContext, useContext, useState, useEffect } from 'react';
import { Auth, Hub, Amplify } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import amplifyConfig from '../amplifyConfig';
import { getUserProfile, getCurrentTenant } from '../services/api/userService';
import { NEW_PASSWORD_REQUIRED_CHALLENGE } from '../constants';
import store from '../reduxStore';

const AuthContext = createContext();

const TENANT_SUCCESS = 'done';

export const useAuth = () => {
	return useContext(AuthContext);
};

function AuthProvider({ children }) {
	const navigate = useNavigate();
	const [user, setUser] = useState(null);
	const [authenticated, setAuthenticated] = useState(false);
	const [initialized, setInitialized] = useState(false);

	useEffect(() => {
		Amplify.configure(amplifyConfig);
		checkUser();
		const handleSignOut = () => {
			setUser(null);
			setAuthenticated(false);
		};

		const authListener = Hub.listen('auth', ({ payload: { event } }) => {
			switch (event) {
				case 'signIn':
					return checkUser();
				case 'signOut':
					return handleSignOut();
				default:
					break;
			}
		});

		return () => authListener();
	}, []);

	const checkUser = async () => {
		try {
			const cognitoData = await Auth.currentAuthenticatedUser({ bypassCache: true });
			const userData = await fetchUserSettings();
			const tenantData = await fetchTenantSettings();
			setInitialized(true);
			if (userData?.status !== 200 || tenantData?.data?.init_status !== TENANT_SUCCESS) {
				navigate('/auth/account-info');
				return;
			}
			setUser({
				...cognitoData.attributes,
				role: userData?.data?.role
			});
			setAuthenticated(true);
		} catch (err) {
			setUser(null);
			setAuthenticated(false);
			setInitialized(true);
		}
	};

	const forceRefreshSession = async () => {
		try {
			const user = await Auth.currentAuthenticatedUser();
			const session = await Auth.currentSession();

			// Force refresh the tokens
			const newSession = await new Promise((resolve, reject) => {
				user.refreshSession(session.getRefreshToken(), (err, newSession) => {
					if (err) {
						reject(err);
					} else {
						resolve(newSession);
					}
				});
			});

			// Manually set the new session tokens
			user.signInUserSession = newSession;

			// Update the Cognito User Pool local storage
			const userCopy = { ...user, signInUserSession: newSession };
			Auth.storeUserData(userCopy);
			return newSession;
		} catch (error) {
			console.log('Error refreshing token:', error);
		}
	};

	const signIn = async (username, password) => {
		const response = await Auth.signIn(username, password);
		if (response.challengeName === NEW_PASSWORD_REQUIRED_CHALLENGE) {
			console.log('New password required', response);
			setUser(response);
		}
		return response;
	};

	const signUp = async (username, password, email) => {
		const response = await Auth.signUp({
			username,
			password,
			attributes: { email }
		});

		if (typeof response.userConfirmed !== 'undefined' && response.userConfirmed === false) {
			response.confirmUser = true;
		}
		return response;
	};

	const signOut = async () => {
		try {
			store.dispatch({ type: 'UNAUTH_USER' });
			await Auth.signOut();
		} catch (err) {
			console.error('Error signing out', err);
		}
	};

	const fetchUserSettings = async () => {
		try {
			const userSettings = await getUserProfile();
			return userSettings;
		} catch (err) {
			console.error('Error fetching user settings', err, err.response);
			return err?.response;
		}
	};

	const fetchTenantSettings = async () => {
		try {
			const tenantSettings = await getCurrentTenant();
			return tenantSettings;
		} catch (err) {
			console.error('Error fetching tenant settings', err, err.response);
			return err?.response;
		}
	};

	const forgotPassword = async username => {
		return Auth.forgotPassword(username);
	};

	const submitForgotPassword = async (username, code, newPassword) => {
		return Auth.forgotPasswordSubmit(username, code, newPassword);
	};

	const completeNewPassword = async newPassword => {
		try {
			await Auth.completeNewPassword(user, newPassword);
		} catch (err) {
			console.error('Error completing new password', err);
		}
	};

	const confirmSignup = async (code, usr) => {
		const confirmUser = Auth.confirmSignUp(usr, code);
		return confirmUser;
	};

	const resendConfirmationCode = async username => {
		return Auth.resendSignUp(username ?? user.email);
	};

	const changePassword = async (oldPassword, newPassword) => {
		const currentUser = await Auth.currentAuthenticatedUser();
		return Auth.changePassword(currentUser, oldPassword, newPassword);
	};

	const getAccessToken = async () => {
		try {
			const session = await Auth.currentSession();
			const token = session.getIdToken().getJwtToken();
			return token;
		} catch (err) {
			console.error('Error getting access token', err, err.response);
		}
	};

	return (
		<AuthContext.Provider
			// eslint-disable-next-line react/jsx-no-constructed-context-values
			value={{
				user: {
					displayName: user?.email,
					role: user?.role,
					isSuperadmin: user?.['custom:superuser'] === 'true',
					tenant: user?.['custom:tenant'],
					email: user?.email,
					...user
				},
				federatedId: process.env.REACT_APP_FEDERATED_ID,
				isAuthenticated: authenticated,
				isInitialized: initialized,
				code: user?.challengeName,
				login: signIn,
				register: signUp,
				logout: signOut,
				resetPassword: forgotPassword,
				submitForgotPassword,
				completeNewPassword,
				resendConfirmationCode,
				validateUserSession: checkUser,
				refreshSession: forceRefreshSession,
				confirmSignup,
				changePassword,
				getAccessToken
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export { AuthContext, AuthProvider };
